<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  vipLevel: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['edit', 'delete', 'toggleActive']);

const { t } = useI18n();

const displayName = computed(() => {
  if (props.vipLevel.min_value && props.vipLevel.max_value) {
    return `${props.vipLevel.name} (${props.vipLevel.min_value}-${props.vipLevel.max_value})`;
  }
  return props.vipLevel.name;
});
</script>

<template>
  <div
    class="vip-row flex items-center justify-between p-4 border rounded-lg"
    :class="{ 'opacity-50': !vipLevel.active }"
  >
    <div class="flex items-center gap-4">
      <!-- VIP Icon Preview -->
      <div v-if="vipLevel.icon_url" class="flex-shrink-0">
        <img
          :src="vipLevel.icon_url"
          :alt="vipLevel.name"
          class="w-10 h-10 rounded-full shadow-md object-cover"
        />
      </div>
      <div
        v-else
        class="flex-shrink-0 w-10 h-10 rounded-full bg-n-slate-3 flex items-center justify-center"
      >
        <fluent-icon icon="star" size="20px" class="text-n-slate-9" />
      </div>

      <!-- VIP Info -->
      <div class="flex-1 min-w-0">
        <div class="flex items-center gap-2">
          <h4 class="vip-name">
            {{ displayName }}
          </h4>
          <span class="vip-level-chip"> VIP {{ vipLevel.level }} </span>
          <span v-if="!vipLevel.active" class="vip-inactive-badge">
            {{ $t('VIP_SETTINGS.INACTIVE') }}
          </span>
        </div>
        <p class="vip-sub">
          {{ $t('VIP_SETTINGS.LEVEL_LABEL') }}: {{ vipLevel.level }}
        </p>
      </div>
    </div>

    <!-- Actions -->
    <div class="flex items-center gap-2">
      <NextButton
        v-tooltip="$t('VIP_SETTINGS.EDIT.BUTTON_TEXT')"
        icon="i-lucide-pencil"
        slate
        faded
        sm
        @click="emit('edit', vipLevel)"
      />
      <NextButton
        v-tooltip="$t('VIP_SETTINGS.DELETE.BUTTON_TEXT')"
        icon="i-lucide-trash-2"
        ruby
        faded
        sm
        @click="emit('delete', vipLevel)"
      />
    </div>
  </div>
</template>

<style scoped>
.vip-row {
  background: #ffffff;
  border-color: #e5e7eb;
  transition: box-shadow 0.12s ease;
}
.vip-row:hover {
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.06);
  border-color: #d1d5db;
}
.vip-avatar-placeholder {
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
}
.vip-name {
  font-size: 13px;
  font-weight: 700;
  color: #111827;
  margin: 0;
}
.vip-sub {
  font-size: 11px;
  color: #6b7280;
  margin: 2px 0 0;
}
.vip-level-chip {
  display: inline-flex;
  align-items: center;
  padding: 1px 8px;
  border-radius: 9999px;
  font-size: 10px;
  font-weight: 700;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  color: #374151;
}
.vip-inactive-badge {
  display: inline-block;
  padding: 1px 6px;
  font-size: 10px;
  background: #f3f4f6;
  color: #6b7280;
  border-radius: 4px;
}
</style>
